import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Download, Mail, Tag, Search, Pencil } from "lucide-react";

export default function DiscountLeads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ notes: "", converted: false });

  const load = async () => {
    try {
      const r = await api.get("/admin/discount-leads");
      setLeads(r.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = leads.filter((l) =>
    !q ? true : (l.email + " " + (l.discount_code || "") + " " + (l.notes || "")).toLowerCase().includes(q.toLowerCase())
  );

  const exportCsv = () => {
    const header = ["email", "discount_code", "submitted_at", "marketing_consent", "converted", "source", "notes"];
    const rows = leads.map((l) => [
      l.email,
      l.discount_code || "",
      (l.created_at || "").slice(0, 19).replace("T", " "),
      l.marketing_consent ? "yes" : "no",
      l.converted ? "yes" : "no",
      l.source || "",
      (l.notes || "").replace(/[\r\n]+/g, " "),
    ]);
    const csv = [header, ...rows].map((r) =>
      r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "trustline-discount-leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const openEdit = (l) => {
    setEditing(l);
    setDraft({ notes: l.notes || "", converted: !!l.converted, marketing_consent: !!l.marketing_consent });
  };

  const save = async () => {
    try {
      const body = { ...draft };
      if (body.marketing_consent === !!editing.marketing_consent) delete body.marketing_consent;
      await api.patch(`/admin/discount-leads/${editing.id}`, body);
      toast.success("Lead updated.");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const stats = {
    total: leads.length,
    converted: leads.filter((l) => l.converted).length,
    marketing: leads.filter((l) => l.marketing_consent).length,
  };

  return (
    <div className="space-y-6" data-testid="admin-discount-leads-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B] flex items-center gap-2">
            <Tag className="w-6 h-6 text-[#5BA83F]" /> Discount Leads
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Visitors who submitted their email through the website pop-up. Marketing consent shown explicitly — do not contact for marketing unless they opted in.
          </p>
        </div>
        <Button onClick={exportCsv} variant="outline" className="rounded-full" data-testid="leads-export-csv">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Total leads" value={stats.total} />
        <Stat label="Converted to customers" value={stats.converted} green />
        <Stat label="Marketing opt-ins" value={stats.marketing} />
      </div>

      <div className="bg-white rounded-xl ring-1 ring-slate-200 p-4 flex items-center gap-3">
        <Search className="w-4 h-4 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email, code or notes…"
          className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          data-testid="leads-search"
        />
      </div>

      <div className="bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Code issued</th>
              <th className="text-left px-4 py-3">Submitted</th>
              <th className="text-left px-4 py-3">Marketing</th>
              <th className="text-left px-4 py-3">Converted</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-slate-400">No discount leads yet.</td></tr>
            )}
            {filtered.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    <a href={`mailto:${l.email}`} className="font-medium text-[#1B3A6B] hover:underline">{l.email}</a>
                  </div>
                  {l.notes && <div className="text-xs text-slate-500 mt-1 line-clamp-1">{l.notes}</div>}
                </td>
                <td className="px-4 py-3">
                  <Badge className="bg-slate-100 text-slate-700 border-0 font-mono">{l.discount_code || "—"}</Badge>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                  {l.created_at?.slice(0, 16).replace("T", " ")}
                </td>
                <td className="px-4 py-3">
                  <Badge className={`border-0 ${l.marketing_consent ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                    {l.marketing_consent ? "Yes" : "No"}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge className={`border-0 ${l.converted ? "bg-[#E8F5E0] text-[#3F7A1F]" : "bg-slate-100 text-slate-600"}`}>
                    {l.converted ? "Yes" : "No"}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{l.source || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(l)} data-testid={`lead-edit-${l.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit lead</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="text-sm">
                <div className="text-slate-500 text-xs uppercase tracking-wide">Email</div>
                <div className="font-medium">{editing.email}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Internal notes</Label>
                <Textarea
                  rows={3}
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  className="mt-1.5"
                  data-testid="lead-edit-notes"
                />
              </div>
              <label className="flex items-center justify-between bg-slate-50 ring-1 ring-slate-200 rounded-lg px-3 py-2.5 cursor-pointer">
                <div className="text-sm">
                  <div className="font-medium">Marketing consent</div>
                  <div className="text-xs text-slate-500">Turn off when the person unsubscribes or withdraws consent.{editing.marketing_withdrawn_at ? ` Withdrawn ${editing.marketing_withdrawn_at.slice(0, 10)}.` : ""}</div>
                </div>
                <Switch
                  checked={!!draft.marketing_consent}
                  onCheckedChange={(v) => setDraft({ ...draft, marketing_consent: v })}
                  data-testid="lead-edit-marketing"
                />
              </label>
              <label className="flex items-center justify-between bg-slate-50 ring-1 ring-slate-200 rounded-lg px-3 py-2.5 cursor-pointer">
                <div className="text-sm">
                  <div className="font-medium">Mark as converted to customer</div>
                  <div className="text-xs text-slate-500">Auto-detected when a booking is created with this email.</div>
                </div>
                <Switch
                  checked={draft.converted}
                  onCheckedChange={(v) => setDraft({ ...draft, converted: v })}
                  data-testid="lead-edit-converted"
                />
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} data-testid="lead-edit-cancel">Cancel</Button>
            <Button onClick={save} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="lead-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, green }) {
  return (
    <div className={`rounded-xl p-4 ring-1 ${green ? "bg-[#E8F5E0] ring-[#5BA83F]/30" : "bg-white ring-slate-200"}`}>
      <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">{label}</div>
      <div className={`font-heading text-3xl font-bold mt-1 ${green ? "text-[#3F7A1F]" : "text-[#1B3A6B]"}`}>{value}</div>
    </div>
  );
}
